import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface Employee {
  _id?: string;
  name: string;
  empNo: string;
  dob: string;
  department: string;
  role: string;
  bu: string;
  esiNo: string;
  whatsappNumber: string;
}

interface MasterRecord {
  _id?: string;
  name: string;
}

@Component({
  selector: 'app-employee-master',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Component],
  templateUrl: './employee-master.component.html',
  styleUrl: './employee-master.component.scss',
})
export class EmployeeMasterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addEmployeeModal') addEmployeeModalRef!: ElementRef<HTMLElement>;
  @ViewChild('payslipListModal') payslipListModalRef!: ElementRef<HTMLElement>;

  employees: Employee[] = [];
  isLoading = false;
  isSaving = false;
  errorMessage = '';

  newEmployee: Employee = this.getEmptyEmployee();

  roles: MasterRecord[] = [];
  bus: MasterRecord[] = [];
  departments: MasterRecord[] = [];

  selectedEmployeeName = '';
  employeePayslips: any[] = [];
  isLoadingPayslips = false;
  payslipsErrorMessage = '';

  tableConfig: VT2_TABLE_CONFIG = {
    tableTitle: 'Employees',
    excelFileName: 'employee-master',
    columnLabels: {
      name: 'Name',
      empNo: 'Employee No.',
      dob: 'Date of Birth',
      department: 'Department',
      role: 'Role',
      bu: 'BU',
      esiNo: 'ESI No.',
      whatsappNumber: 'WhatsApp Number',
    },
    excludeFields: ['_id', 'createdAt'],
    dateFields: ['dob'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit Employee',
        onClick: (row) => this.editEmployee(row),
      },
      {
        icon: '📄',
        label: 'Pay Slips',
        title: 'View Pay Slips',
        onClick: (row) => this.showPayslips(row),
      },
    ],
  };

  payslipTableConfig: VT2_TABLE_CONFIG = {
    tableTitle: '',
    excelFileName: 'employee-payslips',
    columnLabels: {
      documentType: 'Document Type',
      payslipForMonth: 'Payslip Month',
      payslipForYear: 'Payslip Year',
      uploadedBy: 'Uploaded By',
      createdAt: 'Uploaded On',
    },
    // Name/Employee No./ESI No. are the same for every document in this list
    // (shown once in the table title instead), so they're dropped as columns.
    excludeFields: ['_id', 's3Key', 'employeeName', 'empNo', 'esiNo'],
    dateFields: ['createdAt'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
    actionButtons: [
      {
        icon: '👁️',
        label: 'View',
        title: 'View Pay Slip PDF',
        onClick: (row) => this.viewPayslipPdf(row),
      },
    ],
  };

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.loadEmployees();
    this.loadMasterLists();
  }

  ngAfterViewInit(): void {
    // Move modals to <body> so they aren't trapped inside an ancestor with
    // a CSS transform/filter, which breaks position:fixed stacking and makes
    // the modal appear on top visually but be unclickable.
    this.prepareModal(this.addEmployeeModalRef);
    this.prepareModal(this.payslipListModalRef);
  }

  ngOnDestroy(): void {
    this.detachModal(this.addEmployeeModalRef);
    this.detachModal(this.payslipListModalRef);
  }

  private prepareModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) {
      return;
    }
    document.body.appendChild(modalEl);
    // Bootstrap sets aria-hidden="true" on the modal as it starts hiding; if a
    // button inside it still has focus at that point, Chrome blocks it and logs
    // an aria-hidden/focus warning. Blur whatever's focused inside the modal
    // before Bootstrap hides it.
    modalEl.addEventListener('hide.bs.modal', () => {
      if (modalEl.contains(document.activeElement)) {
        (document.activeElement as HTMLElement)?.blur();
      }
    });
  }

  private detachModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (modalEl?.parentNode === document.body) {
      document.body.removeChild(modalEl);
    }
  }

  getEmptyEmployee(): Employee {
    return { name: '', empNo: '', dob: '', department: '', role: '', bu: '', esiNo: '', whatsappNumber: '' };
  }

  private loadMasterLists() {
    this.apiService.getApiCall('api/v1/rolemaster/list').subscribe({
      next: (res: any) => {
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.roles = res.Data || [];
        }
      },
    });
    this.apiService.getApiCall('api/v1/bumaster/list').subscribe({
      next: (res: any) => {
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.bus = res.Data || [];
        }
      },
    });
    this.apiService.getApiCall('api/v1/departmentmaster/list').subscribe({
      next: (res: any) => {
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.departments = res.Data || [];
        }
      },
    });
  }

  loadEmployees() {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall('api/v1/employeemaster/list').subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          // vio-table-v2 derives its columns from the first row's keys only, so older
          // records saved before a field (e.g. whatsappNumber) existed can hide that
          // column entirely. Backfill every field so the key is always present.
          this.employees = (res.Data || []).map((employee: Employee) => ({ ...this.getEmptyEmployee(), ...employee }));
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load employees.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load employees. Please check your connection.';
      },
    });
  }

  resetForAdd() {
    this.newEmployee = this.getEmptyEmployee();
    this.errorMessage = '';
  }

  editEmployee(employee: Employee) {
    this.newEmployee = { ...employee };
    this.errorMessage = '';
    this.openModal(this.addEmployeeModalRef);
  }

  async saveEmployee() {
    if (!this.newEmployee.name || !this.newEmployee.empNo) {
      return;
    }
    const isEdit = !!this.newEmployee._id;
    this.isSaving = true;
    this.errorMessage = '';
    try {
      const endpoint = isEdit ? 'api/v1/employeemaster/update' : 'api/v1/employeemaster/add';
      const payload = isEdit ? { ...this.newEmployee, id: this.newEmployee._id } : { ...this.newEmployee };
      const res = await this.apiService.postApiCallReturningPromise(endpoint, payload);
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        this.newEmployee = this.getEmptyEmployee();
        this.closeModal(this.addEmployeeModalRef);
        this.loadEmployees();
      } else {
        this.errorMessage = res?.errorDescription || `Unable to ${isEdit ? 'update' : 'add'} employee.`;
      }
    } catch (err) {
      this.errorMessage = `Unable to ${isEdit ? 'update' : 'add'} employee.`;
    } finally {
      this.isSaving = false;
    }
  }

  showPayslips(employee: Employee) {
    this.selectedEmployeeName = employee.name;
    this.employeePayslips = [];
    this.payslipsErrorMessage = '';
    this.isLoadingPayslips = true;
    this.payslipTableConfig = {
      ...this.payslipTableConfig,
      tableTitle: `Employee No.: ${employee.empNo}   |   ESI No.: ${employee.esiNo || '-'}`,
    };
    this.openModal(this.payslipListModalRef);

    this.apiService.getApiCall('api/v1/payslip/list', JSON.stringify({ empNo: employee.empNo })).subscribe({
      next: (res: any) => {
        this.isLoadingPayslips = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.employeePayslips = res.Data || [];
        } else {
          this.payslipsErrorMessage = res?.errorDescription || 'Unable to load pay slips.';
        }
      },
      error: () => {
        this.isLoadingPayslips = false;
        this.payslipsErrorMessage = 'Unable to load pay slips. Please check your connection.';
      },
    });
  }

  viewPayslipPdf(payslip: any) {
    this.apiService.getApiCall('api/v1/payslip/view', JSON.stringify({ id: payslip._id })).subscribe({
      next: (res: any) => {
        if (res && res.errorCode === this.apiService.NO_ERROR && res.Data?.url) {
          window.open(res.Data.url, '_blank');
        } else {
          this.payslipsErrorMessage = res?.errorDescription || 'Unable to open the pay slip PDF.';
        }
      },
      error: () => {
        this.payslipsErrorMessage = 'Unable to open the pay slip PDF. Please check your connection.';
      },
    });
  }

  private openModal(modalRef: ElementRef<HTMLElement>) {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) {
      return;
    }
    const modalInstance = bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    modalInstance.show();
  }

  private closeModal(modalRef: ElementRef<HTMLElement>) {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) {
      return;
    }
    const modalInstance = bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    modalInstance.hide();
  }
}
